import React, { useState, useEffect } from 'react'
import {
  Autocomplete,
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  FormHelperText,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
} from '@mui/material'
import { Info as InfoIcon } from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { parseDate } from '../../../../utils/dateUtils'
import { fetchPlantListForDGR, fetchPlantMetrics } from '../../services/dgrService'

const DGRForm = ({
  report,
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState({
    date: parseDate(),
    plantId: '',
    plantName: '',
    plantCapacity: 0,
    plantLocation: '',
    gti: 0,
    actYield: 0,
    actGeneration: 0,
    pr: 0,
    remarks: '',
  })

  const [plants, setPlants] = useState([])
  const [loading, setLoading] = useState(false)
  const [plantLoading, setPlantLoading] = useState(false)
  const [metricsLoading, setMetricsLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [metricsAvailable, setMetricsAvailable] = useState(false)
  const [isHardcodedPlant, setIsHardcodedPlant] = useState(false)
  const [hasSensor, setHasSensor] = useState(true)
  const [isEditMode, setIsEditMode] = useState(false)
  const [originalValues, setOriginalValues] = useState({
    gti: null,
    actYield: null,
    actGeneration: null,
    pr: null,
  })
  // Track whether user edited fields (to decide when to auto-calc PR)
  const [gtiEdited, setGtiEdited] = useState(false)
  const [actGenEdited, setActGenEdited] = useState(false)
  // Calculation display strings
  const [actYieldCalc, setActYieldCalc] = useState('')
  const [prCalc, setPrCalc] = useState('')

  useEffect(() => {
    loadPlants()
  }, [])

  useEffect(() => {
    if (report) {
      setIsEditMode(true)
      setFormData({
        date: parseDate(report.date || report.reportDate),
        plantId: report.plantId || '',
        plantName: report.plantName || '',
        plantCapacity: report.plantCapacity ? parseFloat(parseFloat(report.plantCapacity).toFixed(2)) : 0,
        plantLocation: report.plantLocation || '',
        gti: report.gti ? parseFloat(parseFloat(report.gti).toFixed(2)) : 0,
        actYield: report.actYield ? parseFloat(parseFloat(report.actYield).toFixed(2)) : 0,
        actGeneration: report.actGeneration ? parseFloat(parseFloat(report.actGeneration).toFixed(2)) : 0,
        pr: report.pr ? parseFloat(parseFloat(report.pr).toFixed(2)) : 0,
        remarks: report.remarks || '',
      })

      // Store original values for comparison
      setOriginalValues({
        gti: report.gti ? parseFloat(parseFloat(report.gti).toFixed(2)) : null,
        actYield: report.actYield ? parseFloat(parseFloat(report.actYield).toFixed(2)) : null,
        actGeneration: report.actGeneration ? parseFloat(parseFloat(report.actGeneration).toFixed(2)) : null,
        pr: report.pr ? parseFloat(parseFloat(report.pr).toFixed(2)) : null,
      })
    } else {
      setIsEditMode(false)
      setOriginalValues({
        gti: null,
        actYield: null,
        actGeneration: null,
        pr: null,
      })
    }
  }, [report])

  // Refetch plants when date changes
  useEffect(() => {
    if (formData.date) {
      const reportDate = formData.date.format('YYYY-MM-DD')
      loadPlants(reportDate)
    }
  }, [formData.date])

  // Auto-fetch metrics when plant and date change (only for new reports, not editing)
  useEffect(() => {
    if (formData.plantId && formData.date && !isEditMode) {
      fetchMetrics()
    }
  }, [formData.plantId, formData.date, isEditMode])

  const loadPlants = async (reportDate = null) => {
    try {
      setPlantLoading(true)
      const plantList = await fetchPlantListForDGR(reportDate)
      setPlants(plantList)
    } catch (error) {
      console.error('Error loading plants:', error)
    } finally {
      setPlantLoading(false)
    }
  }

  const fetchMetrics = async () => {
    if (!formData.plantId || !formData.date) return

    try {
      setMetricsLoading(true)
      const reportDate = formData.date.format('YYYY-MM-DD')
      const response = await fetchPlantMetrics(formData.plantId, reportDate)

      if (response && response.metrics && Object.keys(response.metrics).length > 0) {
        // Store original values before updating form
        const fetchedGti = response.metrics.gti ? parseFloat(parseFloat(response.metrics.gti).toFixed(2)) : 0
        const fetchedActYield = response.metrics.actYield ? parseFloat(parseFloat(response.metrics.actYield).toFixed(2)) : 0
        const fetchedActGeneration = response.metrics.actGeneration ? parseFloat(parseFloat(response.metrics.actGeneration).toFixed(2)) : 0
        const fetchedPr = response.metrics.pr ? parseFloat(parseFloat(response.metrics.pr).toFixed(2)) : 0

        setOriginalValues({
          gti: fetchedGti,
          actYield: fetchedActYield,
          actGeneration: fetchedActGeneration,
          pr: fetchedPr,
        })

        // Metrics available - populate the fields and round to 2 decimals
        setFormData((prev) => ({
          ...prev,
          gti: fetchedGti,
          actYield: fetchedActYield,
          actGeneration: fetchedActGeneration,
          pr: fetchedPr,
        }))
        // Reset user-edited flags and calculation displays when metrics are auto-populated
        setGtiEdited(false)
        setActGenEdited(false)
        setActYieldCalc('')
        setPrCalc('')
        setMetricsAvailable(true)
        setIsHardcodedPlant(response.isHardcoded || false)
      } else {
        // No metrics available - just mark it
        setMetricsAvailable(false)
        const hardcoded = response?.isHardcoded || false
        setIsHardcodedPlant(hardcoded)
        setOriginalValues({
          gti: null,
          actYield: null,
          actGeneration: null,
          pr: null,
        })

        // If plant is hardcoded (no metrics), clear numeric fields so UI shows empty inputs
        if (hardcoded) {
          setFormData((prev) => ({
            ...prev,
            gti: '',
            actYield: '',
            actGeneration: '',
            pr: '',
          }))
          // clear calc displays and edited flags
          setGtiEdited(false)
          setActGenEdited(false)
          setActYieldCalc('')
          setPrCalc('')
        }
      }
    } catch (error) {
      console.error('Error fetching metrics:', error)
      setMetricsAvailable(false)
      setOriginalValues({
        gti: null,
        actYield: null,
        actGeneration: null,
        pr: null,
      })

      // On error treat as no metrics; do not populate zeros — clear numeric fields
      setFormData((prev) => ({
        ...prev,
        gti: '',
        actYield: '',
        actGeneration: '',
        pr: '',
      }))
      setGtiEdited(false)
      setActGenEdited(false)
      setActYieldCalc('')
      setPrCalc('')
    } finally {
      setMetricsLoading(false)
    }
  }

  const handleInputChange = (field, value) => {
    // Convert numeric fields to numbers
    let convertedValue = value
    if (['gti', 'actYield', 'actGeneration', 'pr', 'plantCapacity'].includes(field)) {
      if (value === '' || value === null) {
        convertedValue = ''
      } else {
        // Parse and round to 2 decimal places
        const numValue = parseFloat(value)
        if (!isNaN(numValue)) {
          convertedValue = parseFloat(numValue.toFixed(2))
        } else {
          convertedValue = ''
        }
      }
    }

    setFormData((prev) => ({
      ...prev,
      [field]: convertedValue,
    }))

    // Auto-calculations
    // When user edits actGeneration -> compute actYield
    if (field === 'actGeneration') {
      setActGenEdited(true)

      const actGenVal = convertedValue === '' ? null : Number(convertedValue)
      const plantCap = Number(formData.plantCapacity) || 0

      if (actGenVal !== null && !isNaN(actGenVal) && plantCap > 0) {
        const newActYield = parseFloat((actGenVal / plantCap).toFixed(2))
        setFormData((prev) => ({ ...prev, actYield: newActYield }))
        setActYieldCalc(`${actGenVal} kWh ÷ ${plantCap} kWp = ${newActYield.toFixed(2)} kWh/kWp`)
      } else {
        setFormData((prev) => ({ ...prev, actYield: '' }))
        setActYieldCalc('')
      }

      // If GTI was also edited by user, calculate PR
      if (gtiEdited) {
        const gtiVal = Number(formData.gti)
        if (actGenVal !== null && !isNaN(actGenVal) && gtiVal > 0) {
          const newPr = parseFloat(((actGenVal / gtiVal) * 100).toFixed(2))
          setFormData((prev) => ({ ...prev, pr: newPr }))
          setPrCalc(`(${actGenVal} kWh ÷ ${gtiVal} W/m²) × 100 = ${newPr.toFixed(2)} %`)
        } else {
          setFormData((prev) => ({ ...prev, pr: '' }))
          setPrCalc('')
        }
      }
    }

    // When user edits GTI -> mark edited and compute PR only if actGeneration was edited
    if (field === 'gti') {
      setGtiEdited(true)
      const gtiVal = convertedValue === '' ? null : Number(convertedValue)
      const actGenVal = Number(formData.actGeneration)
      if (actGenEdited && actGenVal !== '' && !isNaN(actGenVal) && gtiVal !== null && !isNaN(gtiVal) && gtiVal > 0) {
        const newPr = parseFloat(((actGenVal / gtiVal) * 100).toFixed(2))
        setFormData((prev) => ({ ...prev, pr: newPr }))
        setPrCalc(`(${actGenVal} kWh ÷ ${gtiVal} W/m²) × 100 = ${newPr.toFixed(2)} %`)
      } else {
        setFormData((prev) => ({ ...prev, pr: prev.pr }))
      }
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }))
    }
  }

  const handlePlantChange = (selectedPlant) => {
    if (!selectedPlant) {
      setFormData((prev) => ({
        ...prev,
        plantId: '',
        plantName: '',
        plantCapacity: 0,
        plantLocation: '',
      }))
      setHasSensor(true)
      return
    }

    setFormData((prev) => ({
      ...prev,
      plantId: selectedPlant.id || selectedPlant._id,
      plantName: selectedPlant.plantName,
      plantCapacity: selectedPlant.plantCapacity || selectedPlant.plantSize || 0,
      plantLocation: selectedPlant.plantLocation,
    }))

    // Store the sensor property
    setHasSensor(selectedPlant.sensor !== false)
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.date) {
      newErrors.date = 'Date is required'
    }

    if (!formData.plantId) {
      newErrors.plantId = 'Plant selection is required'
    }

    // Only validate GTI and PR if plant has sensor
    if (hasSensor) {
      if (isNaN(formData.gti) || formData.gti < 0) {
        newErrors.gti = 'GTI must be a positive number'
      }

      if (formData.pr && (isNaN(formData.pr) || formData.pr < 0 || formData.pr > 100)) {
        newErrors.pr = 'PR must be between 0 and 100'
      }
    }

    if (isNaN(formData.actYield) || formData.actYield < 0) {
      newErrors.actYield = 'Actual Yield must be a positive number'
    }

    if (isNaN(formData.actGeneration) || formData.actGeneration < 0) {
      newErrors.actGeneration = 'Actual Generation must be a positive number'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setLoading(true)
    try {
      // Helper function to safely format numbers to 2 decimal places
      const formatToTwoDecimals = (value) => {
        if (value === '' || value === null || value === undefined || isNaN(value)) {
          return 0
        }
        return parseFloat(parseFloat(value).toFixed(2))
      }

      const submitData = {
        ...formData,
        reportDate: formData.date.toISOString(),
        plantCapacity: formatToTwoDecimals(formData.plantCapacity),
        gti: formatToTwoDecimals(formData.gti),
        actYield: formatToTwoDecimals(formData.actYield),
        actGeneration: formatToTwoDecimals(formData.actGeneration),
        pr: formatToTwoDecimals(formData.pr),
      }
      // Remove the date field since we're using reportDate
      delete submitData.date

      await onSubmit(submitData)
    } catch (error) {
      console.error('Error submitting form:', error)
    } finally {
      setLoading(false)
    }
  }

  // Ensure numeric fields render 0 explicitly (not as empty string)
  const getDisplayValue = (value) => {
    return value === '' || value === null || value === undefined ? '' : value
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Grid container spacing={3}>
        {/* Date and Plant Selection */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Report Information
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Report Date"
              format="DD-MM-YYYY"
              value={formData.date}
              onChange={(date) => handleInputChange('date', date)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!errors.date,
                  helperText: errors.date,
                },
              }}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={12} md={6}>
          <Autocomplete
            fullWidth
            disablePortal
            loading={plantLoading}
            options={plants}
            getOptionLabel={(option) =>
              option.projectCode
                ? `${option.plantName} - ${option.projectCode}`
                : option.plantName
            }
            value={plants.find((p) => (p.id || p._id) === formData.plantId) || null}
            onChange={(e, newValue) =>
              handlePlantChange(newValue)
            }
            isOptionEqualToValue={(option, value) => (option.id || option._id) === (value.id || value._id)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Plant"
                required
                error={!!errors.plantId}
                helperText={errors.plantId}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {plantLoading ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            noOptionsText={
              plantLoading ? 'Fetching plants...' : 'No plants found.'
            }
            filterOptions={(options, { inputValue }) =>
              options.filter((plant) =>
                `${plant.plantName} ${plant.projectCode || ''}`
                  .toLowerCase()
                  .includes(inputValue.toLowerCase())
              )
            }
          />
        </Grid>

        {/* Plant Details Display */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Plant Details
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Plant Name"
            value={formData.plantName}
            disabled
            variant="outlined"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Plant Location"
            value={formData.plantLocation}
            disabled
            variant="outlined"
            inputProps={{
              style: {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
              title: formData.plantLocation || '',
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Plant Capacity (kWp)"
            value={formData.plantCapacity}
            disabled
            variant="outlined"
          />
        </Grid>

        {/* Auto-fetch Metrics Status */}
        {metricsLoading && !isEditMode && (
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="textSecondary">
                Fetching metrics...
              </Typography>
            </Box>
          </Grid>
        )}

        {metricsAvailable && !isEditMode && (
          <Grid item xs={12}>
            <Alert severity="info">
              Metrics auto-populated from database. You can edit them if needed.
            </Alert>
          </Grid>
        )}

        {isHardcodedPlant && !metricsAvailable && !isEditMode && (
          <Grid item xs={12}>
            <Alert severity="warning">
              This is a hardcoded plant. Please enter metrics manually.
            </Alert>
          </Grid>
        )}

        {!hasSensor && !isEditMode && (
          <Grid item xs={12}>
            <Alert severity="info">
              This plant doesn't have a sensor. GTI and PR fields are not available.
            </Alert>
          </Grid>
        )}

        {/* Generation Data */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Generation Data {isEditMode ? '(Stored Values)' : '(Editable)'}
          </Typography>
        </Grid>

        {hasSensor && (
          <Grid item xs={12} md={6}>
            <Box display="flex" alignItems="flex-start" gap={1}>
              <TextField
                fullWidth
                label="GTI (Global Tilted Irradiance)"
                type="number"
                value={getDisplayValue(formData.gti)}
                onChange={(e) => handleInputChange('gti', e.target.value)}
                error={!!errors.gti}
                helperText={errors.gti || 'W/m²'}
                inputProps={{ step: '0.01', min: '0' }}
                required
              />
              {originalValues.gti !== null && formData.gti !== originalValues.gti && (
                <Tooltip title={`Original value: ${originalValues.gti}`}>
                  <IconButton size="small" sx={{ mt: 1 }}>
                    <InfoIcon fontSize="small" color="info" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Grid>
        )}

        <Grid item xs={12} md={6}>
          <Box display="flex" alignItems="flex-start" gap={1}>
            <TextField
              fullWidth
              label="Act Yield (kWh/kWp)"
              type="number"
              value={getDisplayValue(formData.actYield)}
              error={!!errors.actYield}
              helperText={errors.actYield || actYieldCalc}
              inputProps={{ step: '0.01', min: '0', readOnly: true }}
              required
            />
            {originalValues.actYield !== null && formData.actYield !== originalValues.actYield && (
              <Tooltip title={`Original value: ${originalValues.actYield}`}>
                <IconButton size="small" sx={{ mt: 1 }}>
                  <InfoIcon fontSize="small" color="info" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box display="flex" alignItems="flex-start" gap={1}>
            <TextField
              fullWidth
              label="Act Generation (kWh)"
              type="number"
              value={getDisplayValue(formData.actGeneration)}
              onChange={(e) => handleInputChange('actGeneration', e.target.value)}
              error={!!errors.actGeneration}
              helperText={errors.actGeneration}
              inputProps={{ step: '0.01', min: '0' }}
              required
            />
            {originalValues.actGeneration !== null && formData.actGeneration !== originalValues.actGeneration && (
              <Tooltip title={`Original value: ${originalValues.actGeneration}`}>
                <IconButton size="small" sx={{ mt: 1 }}>
                  <InfoIcon fontSize="small" color="info" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Grid>

        {hasSensor && (
          <Grid item xs={12} md={6}>
            <Box display="flex" alignItems="flex-start" gap={1}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <TextField
                  fullWidth
                  label="PR (Performance Ratio %)"
                  type="number"
                  value={getDisplayValue(formData.pr)}
                  inputProps={{ step: '0.01', min: '0', max: '100', readOnly: true }}
                // helperText="%"
                />
                {prCalc && (
                  <FormHelperText sx={{ ml: 1 }}>{prCalc}</FormHelperText>
                )}
              </Box>
              {originalValues.pr !== null && formData.pr !== originalValues.pr && (
                <Tooltip title={`Original value: ${originalValues.pr}`}>
                  <IconButton size="small" sx={{ mt: 1 }}>
                    <InfoIcon fontSize="small" color="info" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Grid>
        )}

        {/* Remarks */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Remarks"
            value={formData.remarks}
            onChange={(e) => handleInputChange('remarks', e.target.value)}
            multiline
            rows={4}
            placeholder="Add any additional remarks or notes"
          />
        </Grid>

        {/* Form Actions */}
        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
            <Button variant="outlined" onClick={onCancel} disabled={loading || metricsLoading}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading || metricsLoading}
            >
              {loading ? 'Saving...' : report ? 'Update Report' : 'Create Report'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}

export default DGRForm
